<script>
  import { colors } from './colors.js'
  import {
    formatThousands,
    formatDateLong,
    formatDateAxis,
    formatMillions,
    formatMillionsLabels,
  } from './functions'
  import { scaleLinear, scaleTime } from 'd3-scale'
  import { max, extent } from 'd3-array'
  // import { line, curveNatural } from 'd3-shape'
  import { onMount, getContext } from 'svelte'
  import Line from './components/Line.svelte'
  import Points from './components/Points.svelte'
  import Axis from './components/Axis.svelte'
  import Grid from './components/Grid.svelte'

  let { nData, margin } = getContext('chart')
  let nationalData = nData()
  let millions = []
  let width
  let height = 300
  let latestVaccCount
  let latestDate
  let lastDays = ''
  let xScale
  let yScale
  // let line_gen
  let xTicks
  let yTicks

  onMount(async () => {
    addLineChart()
    setTimeout(() => {
      lastDays =
        (millions[millions.length - 1].Date -
          millions[millions.length - 2].Date) /
        (1000 * 3600 * 24)
    }, 500)
  })

  $: xScale = scaleTime()
    .range([margin.left, width - margin.right])
    .domain(extent(nationalData, (d) => d.Date))

  $: yScale = scaleLinear()
    .range([height - margin.bottom, margin.top])
    .domain([0, max(nationalData, (d) => +d.total_vaccinations + 100000)])

  // $: line_gen = line()
  //   .curve(curveNatural)
  //   .x((d) => xScale(d.Date))
  //   .y((d) => yScale(d.total_vaccinations))

  $: xTicks = xScale.ticks(5)
  $: yTicks = yScale.ticks(5)

  // get million points

  let millionGaps = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26]
  let mm = max(nationalData, (d) => d.total_vaccinations) / 1000000

  for (let i = 1; i < mm; i++) {
    let c = nationalData.filter(
      (d) =>
        d.total_vaccinations > i * 1000000 &&
        d.total_vaccinations < (i + 1) * 1000000
    )

    let b = c.sort((a, b) =>
      a.total_vaccinations < b.total_vaccinations ? -1 : 1
    )
    millions.push(b[0])
  }

  function addLineChart() {
    latestVaccCount = nationalData[0].total_vaccinations
    latestDate = nationalData[0].Date
  }

  function getDaysDifference(i) {
    let diff
    if (i > 0) {
      diff = millions[i].Date - millions[i - 1].Date
      return diff / (1000 * 3600 * 24)
    } else {
      return ''
    }
  }
</script>

<div class="chart-wrap">
  <div class="chart-title">How many days to do a million vaccinations?</div>
  <div class="chart chart-cumulative chart-block" bind:clientWidth={width}>
    <img src="./assets/up-arrow.png" alt="arrow" class="arrow" />
    <div class="arrow-text">
      {lastDays} days to do last million vaccinations
    </div>
    <div class="chart-label">
      <div class="chart-label-bold">{formatThousands(latestVaccCount)}</div>
      {formatDateLong(latestDate)}
    </div>
    <svg {width} {height}>
      <Axis
        type="y"
        scale={{ x: xScale, y: yScale }}
        ticks={{ x: xTicks, y: yTicks }}
        {height}
        {width}
      />
      <Axis
        type="x"
        scale={{ x: xScale, y: yScale }}
        ticks={{ x: xTicks, y: yTicks }}
        {height}
        {width}
      />
      <Line
        scale={{ x: xScale, y: yScale }}
        stroke={{ color: colors['green700'], width: 2 }}
        values={{ x: 'Date', y: 'total_vaccinations' }}
        data={nationalData}
        {height}
        {width}
      />

      <!-- ADD MILLION MARKERS -->
      {#each millionGaps as m}
        <rect
          class="million-gap"
          x={xScale(millions[m].Date)}
          y={margin.top}
          width={xScale(millions[m + 1].Date) - xScale(millions[m].Date)}
          height={height - margin.top - margin.bottom}
        />
      {/each}

      <!-- <Grid
        type="y"
        ticks={{ x: xTicks, y: yTicks }}
        scale={{ x: xScale, y: yScale }}
        {height}
        {width}
      /> -->

      <Points
        scale={{ x: xScale, y: yScale }}
        data={millions}
        values={{ x: 'Date', y: 'total_vaccinations' }}
        stroke="#fff"
        strokeWidth="1"
        fill={colors['red500']}
        radius="5"
      />

      <!-- Add million days labels -->
      {#each millions as m, i}
        <text
          class="million-days"
          x={xScale(m.Date)}
          y={yScale(m.total_vaccinations) + 15}>{getDaysDifference(i)}</text
        >
      {/each}

      {#each millions.slice(0, -1) as m, i}
        <text class="million-days million-days-small" x={xScale(m.Date)} y={30}
          >{formatMillionsLabels(m.total_vaccinations)}</text
        >
      {/each}
    </svg>
  </div>
</div>

<style>
  .million-days {
    font-size: 0.7rem;
    text-anchor: middle;
  }
  .million-days-small {
    font-size: 0.5rem;
  }
  .million-gap {
    fill: rgba(245, 245, 245, 0.623);
  }
  .circle-marker {
    fill: var(--darkgreen);
    stroke: #fff;
    stroke-width: 2px;
  }
  .grid-line {
    stroke-width: 1px;
    stroke-dasharray: 2, 2;
    stroke: lightgray;
  }
  .tick-line {
    stroke: gray;
    stroke-width: 1px;
  }

  .chart-label {
    width: 100%;
    position: absolute;
    top: 0px;
    right: 0px;
    line-height: 0.9rem;
    font-size: 0.7rem;
    text-align: right;
    transform: translate(0, -10px);
    padding-right: 10px;
  }
  .chart-label-bold {
    font-weight: 700;
    font-size: 1rem;
  }
  .chart {
    position: relative;
    font-family: 'Titillium Web', Arial, Helvetica, sans-serif;
    width: 100%;

    min-height: 250px;
    margin: 0 auto;
    width: 100%;
  }
  .arrow {
    stroke: #578585;
    fill: #578585;
    width: 20px;
    height: 60px;
    position: absolute;
    right: 30px;
    top: 60px;
    display: none;
  }
  .arrow-text {
    position: absolute;
    right: 0px;
    top: 120px;
    max-width: 150px;
    line-height: 0.9rem;
    color: #578585;
  }
</style>
